import type {
  InvoicePayment,
  Product,
  User,
  UserBalances,
} from 'payments-api/model';
import clsx from 'clsx';
import { useFlagEnabledWithOverrides } from 'hooks/useFlagEnabledWithOverrides';
import { InvoiceLineItemType } from 'payments-api/model/invoiceLineItemType';
import React, { useEffect, useMemo, useState } from 'react';

import type { CurrencyCode } from '@fragment/types/currencyCodes';
import { currencyPrecision as currencyPrecisionMap } from '@fragment/types/currencyCodes';
import {
  kLabelColumnWidth,
  LayoutRow,
  TextCell,
} from '@fragment/ui/src/components/Payments/LayoutRow';
import { TabsV2 } from '@fragment/ui/src/components/Tabs/TabsV2';

import type { ItemTableItem } from './ItemTable';
import { DirectionArrowCells } from './DirectionArrowCells';
import { FormatBillingAmount } from './formatBillingAmount';
import { InvoiceSummaryTab } from './InvoiceSummaryTab';
import { ItemTable } from './ItemTable';
import { PaymentFlowsTable } from './PaymentFlowsTable';
import { UserLabelCell } from './UserLabelCell';

const tabOptions = [
  { label: 'Summary', value: 'summary' as const },
  { label: 'Items', value: 'items' as const },
  { label: 'Payments', value: 'payments' as const },
];

type TabId = 'summary' | 'items' | 'payments';

export type InvoiceTotals = {
  totalAmount: { amount: number; currency: CurrencyCode }[];
  isTotalBalanced: boolean;
};

export const getTotalsFromItems = (items: ItemTableItem[]): InvoiceTotals => {
  const totalPayinAmount = items
    .filter((item) => item.type === InvoiceLineItemType.payin)
    .reduce((acc, item) => {
      const code = item.currencyCode;
      acc[code] = (acc[code] || 0) + Number(item.amount);
      return acc;
    }, {} as Record<CurrencyCode, number>);

  const totalPayoutAmount = items
    .filter((item) => item.type === InvoiceLineItemType.payout)
    .reduce((acc, item) => {
      const code = item.currencyCode;
      acc[code] = (acc[code] || 0) + Number(item.amount);
      return acc;
    }, {} as Record<CurrencyCode, number>);

  const allCurrencies = new Set([
    ...Object.keys(totalPayinAmount),
    ...Object.keys(totalPayoutAmount),
  ]);
  const isTotalBalanced = Array.from(allCurrencies).every(
    (currency) =>
      (totalPayinAmount[currency as CurrencyCode] || 0) ===
      (totalPayoutAmount[currency as CurrencyCode] || 0)
  );

  const totalAmount = Object.entries(totalPayinAmount).map(
    ([currency, amount]) => ({
      amount: Number(amount),
      currency: currency as CurrencyCode,
    })
  );

  return { totalAmount, isTotalBalanced };
};

/**
 * Totals for the amount row when viewing as a specific user.
 * - If user has both payin and payout: total = payin - payout per currency.
 * - Otherwise: total = absolute value of payin total or payout total per currency.
 */
export const getTotalsForUserView = (
  items: ItemTableItem[]
): {
  amount: number;
  currency: CurrencyCode;
  payinSum: number;
  payoutSum: number;
}[] => {
  const totalPayin = items
    .filter((item) => item.type === InvoiceLineItemType.payin)
    .reduce((acc, item) => {
      const code = item.currencyCode;
      acc[code] = (acc[code] || 0) + Number(item.amount);
      return acc;
    }, {} as Record<CurrencyCode, number>);

  const totalPayout = items
    .filter((item) => item.type === InvoiceLineItemType.payout)
    .reduce((acc, item) => {
      const code = item.currencyCode;
      acc[code] = (acc[code] || 0) + Number(item.amount);
      return acc;
    }, {} as Record<CurrencyCode, number>);

  const hasPayin = items.some(
    (item) => item.type === InvoiceLineItemType.payin
  );
  const hasPayout = items.some(
    (item) => item.type === InvoiceLineItemType.payout
  );
  const hasBoth = hasPayin && hasPayout;

  const currencies = new Set<CurrencyCode>([
    ...(Object.keys(totalPayin) as CurrencyCode[]),
    ...(Object.keys(totalPayout) as CurrencyCode[]),
  ]);

  return Array.from(currencies).map((currency) => {
    const payinSum = totalPayin[currency] || 0;
    const payoutSum = totalPayout[currency] || 0;
    const amount = hasBoth
      ? payinSum - payoutSum
      : Math.abs(payinSum || payoutSum);
    return { amount, currency, payinSum, payoutSum };
  });
};

type InvoiceItemsSectionProps = {
  invoiceId: string;
  items: ItemTableItem[];
  products: Product[];
  users: User[];
  onAddItem?: (item: Omit<ItemTableItem, 'id'>) => void;
  onUpdateItem?: (id: string, item: Omit<ItemTableItem, 'id'>) => void;
  onRemoveItem?: (id: string) => void;
  onUserClick?: (userId: string) => void;
  onItemClick?: (item: ItemTableItem) => void;
  payments?: InvoicePayment[];
  userBalances?: UserBalances[];
  showUnbalancedWarning?: boolean;
  children?: React.ReactNode;
  defaultTab?: TabId;
};

type UserPayment = {
  transactionExternalId: string;
  amount: string;
  currency: CurrencyCode;
};

const PaymentUserGroup = ({
  userId,
  payments,
  users,
  onUserClick,
}: {
  userId: string;
  payments: UserPayment[];
  users: User[];
  onUserClick?: (userId: string) => void;
}) => {
  const user = users.find((u) => u.external_id === userId);
  return (
    <div className="flex flex-row w-full border-b-[0.5px] border-main-200 h-fit">
      <UserLabelCell
        externalId={userId}
        role={user?.role}
        onClick={onUserClick}
        align="start"
      />
      <div className="flex flex-col flex-1 min-w-0">
        {payments.length === 0 ? (
          <div className="flex items-center h-f4 px-[13.5px]">
            <span className="text-main-500 text-xmd">None</span>
          </div>
        ) : (
          payments.map((payment) => {
            const precision = currencyPrecisionMap[payment.currency] || 2;
            return (
              <div
                key={payment.transactionExternalId}
                className="flex flex-row w-full items-center border-b-[0.5px] border-main-200 last:border-b-0 h-f4 px-[13.5px]"
              >
                <span className="text-main text-xmd flex-1 basis-0 truncate">
                  {payment.transactionExternalId}
                </span>
                <div className="flex-1 basis-0 flex justify-end">
                  <FormatBillingAmount
                    amount={Number(payment.amount)}
                    currencyPrecision={precision}
                    currencyCode={payment.currency}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const ReconciledTransactionsTable = ({
  payments,
  userBalances,
  users,
  onUserClick,
}: {
  payments: InvoicePayment[];
  userBalances?: UserBalances[];
  users: User[];
  onUserClick?: (userId: string) => void;
}) => {
  const paymentsByUser = useMemo(() => {
    const map = new Map<string, UserPayment[]>();
    // Initialize all users from userBalances so everyone shows up
    userBalances?.forEach((ub) => {
      if (!map.has(ub.id)) map.set(ub.id, []);
    });
    payments.forEach((payment) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- grandfathered
      const userId = payment.user.external_id ?? payment.user.id;
      const existing = map.get(userId) ?? [];
      existing.push({
        transactionExternalId: payment.transaction.external_id,
        amount: payment.amount,
        currency: payment.currency,
      });
      map.set(userId, existing);
    });
    return map;
  }, [payments, userBalances]);

  return (
    <div className="flex flex-col h-fit w-full">
      {userBalances && userBalances.length > 0 && (
        <div className="flex flex-row w-full items-center border-b-[0.5px] border-main-200 h-f4">
          <span
            className={clsx(
              kLabelColumnWidth,
              'shrink-0 text-main-500 text-xmd px-[13.5px]'
            )}
          >
            Users
          </span>
          <div className="flex flex-row flex-1 min-w-0">
            <span className="text-main-500 text-xmd px-[13.5px] flex-1 basis-0 flex items-center">
              Transaction
            </span>
            <span className="text-main-500 text-xmd px-[13.5px] flex-1 basis-0 flex items-center justify-end">
              Amount
            </span>
          </div>
        </div>
      )}
      {userBalances?.map((ub) => (
        <PaymentUserGroup
          key={ub.id}
          userId={ub.id}
          payments={paymentsByUser.get(ub.id) ?? []}
          users={users}
          onUserClick={onUserClick}
        />
      ))}
    </div>
  );
};

export const InvoiceItemsSection = ({
  invoiceId,
  items,
  products,
  users,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  onUserClick,
  onItemClick,
  payments,
  userBalances,
  showUnbalancedWarning = true,
  children,
  defaultTab = 'summary',
}: InvoiceItemsSectionProps): JSX.Element => {
  const [activeTab, setActiveTab] = useState<TabId>(defaultTab);
  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);
  const isPaymentsForReal = useFlagEnabledWithOverrides(
    'dashboard-payments-for-real'
  );

  const { isTotalBalanced } = useMemo(() => getTotalsFromItems(items), [items]);

  const totalAmountForUserView = useMemo(
    () => getTotalsForUserView(items),
    [items]
  );

  return (
    <>
      <LayoutRow
        height="small"
        justify="start"
        hide={!(showUnbalancedWarning && !isTotalBalanced)}
      >
        <TextCell
          text="Errors"
          textSize="xmd"
          align="left"
          border="right"
          textType="secondary"
        />
        <TextCell
          text="Invoice unbalanced - Pay ins ≠ Pay outs"
          textSize="xmd"
          fullWidth
          align="left"
          border="none"
          textType="warning"
        />
      </LayoutRow>

      <div className="flex items-end w-full">
        <TabsV2
          options={tabOptions}
          selectedValue={activeTab}
          onClick={setActiveTab}
          size="double"
        />
        <div className="flex-1 border-b-[0.5px] border-main-300" />
      </div>

      {activeTab === 'summary' && (
        <InvoiceSummaryTab
          users={users}
          userBalances={userBalances ?? []}
          items={items}
          payments={payments ?? []}
          onUserClick={onUserClick}
        />
      )}

      {activeTab === 'items' && (
        <>
          <ItemTable
            data-testid="payin-items-table"
            items={items}
            products={products}
            users={users}
            onAddItem={onAddItem}
            onUpdateItem={onUpdateItem}
            onRemoveItem={onRemoveItem}
            onUserClick={onUserClick}
            onItemClick={onItemClick}
          />

          <div className="w-full border-t-[0.5px] border-main-300">
            <div className="flex items-center border-b-[0.5px] border-main-200 w-full h-f4 px-[13.5px]">
              <span className="text-main-500 text-xmd">Totals</span>
            </div>
            <div className="flex items-stretch border-b-[0.5px] border-main-200 w-full min-h-[2lh]">
              <div
                className={clsx(
                  kLabelColumnWidth,
                  'shrink-0 border-r-[0.5px] border-main-200'
                )}
                aria-hidden
              />
              <DirectionArrowCells direction="payin" />

              <div className="flex flex-row justify-between items-center gap-x-f1 px-[13.5px] flex-1 min-w-0">
                <span className="text-xmd text-main-500">Payins</span>
                <span className="flex flex-row justify-end gap-x-f1">
                  {totalAmountForUserView.map((amount) => {
                    const precision = currencyPrecisionMap[amount.currency];
                    return (
                      <FormatBillingAmount
                        key={amount.currency}
                        amount={amount.payinSum}
                        currencyCode={amount.currency}
                        currencyPrecision={precision}
                      />
                    );
                  })}
                </span>
              </div>
            </div>
            <div className="flex items-stretch border-b-[0.5px] border-main-200 w-full min-h-[2lh]">
              <div
                className={clsx(
                  kLabelColumnWidth,
                  'shrink-0 border-r-[0.5px] border-main-200'
                )}
                aria-hidden
              />
              <DirectionArrowCells direction="payout" />
              <div className="flex flex-row justify-between items-center gap-x-f1 px-[13.5px] flex-1 min-w-0">
                <span className="text-xmd text-main-500">Payouts</span>
                <span className="flex flex-row justify-end gap-x-f1">
                  {totalAmountForUserView.map((amount) => {
                    const precision = currencyPrecisionMap[amount.currency];
                    return (
                      <FormatBillingAmount
                        key={amount.currency}
                        amount={amount.payoutSum}
                        currencyCode={amount.currency}
                        currencyPrecision={precision}
                      />
                    );
                  })}
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'payments' &&
        (isPaymentsForReal ? (
          <PaymentFlowsTable invoiceId={invoiceId} users={users} />
        ) : (
          <ReconciledTransactionsTable
            payments={payments ?? []}
            userBalances={userBalances}
            users={users}
            onUserClick={onUserClick}
          />
        ))}

      {children}
    </>
  );
};
